import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Mail, Clock, Heart, Shield } from 'lucide-react';

export const SupportPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#FBF9F6' }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-8 transition-opacity hover:opacity-70"
          style={{ color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}
          data-testid="back-button"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        {/* Title */}
        <div className="text-center mb-12">
          <h1 
            className="text-3xl md:text-4xl font-light mb-4"
            style={{ color: '#1A1A2E', fontFamily: 'Cormorant Garamond, serif' }}
          >
            Member Support Policy
          </h1>
          <p style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}>
            Mindful Renewal Coaching
          </p>
        </div>

        {/* How Support Works */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(201,168,76,0.15)' }}
            >
              <Clock className="w-5 h-5" style={{ color: '#C9A84C' }} />
            </div>
            <h2 
              className="text-xl font-medium"
              style={{ color: '#1A1A2E', fontFamily: 'Jost, sans-serif' }}
            >
              How Support Works
            </h2>
          </div>
          
          <div 
            className="p-6 rounded-2xl"
            style={{ background: '#F9F4F0', border: '1px solid #F3EEE8' }}
          >
            <p 
              className="text-lg mb-6 leading-relaxed"
              style={{ color: '#1A1A2E', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
            >
              When you reach out, you will hear back. That's the commitment.
            </p>
            
            <div className="space-y-4">
              <div 
                className="p-4 rounded-xl"
                style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.15)' }}
              >
                <p style={{ color: '#C9A84C', fontFamily: 'Jost, sans-serif', fontWeight: '500', marginBottom: '4px' }}>
                  Annual Members
                </p>
                <p style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}>
                  Response within <strong style={{ color: '#1A1A2E' }}>48 hours</strong>, Monday through Friday
                </p>
              </div>
              
              <div 
                className="p-4 rounded-xl"
                style={{ background: '#FAF7F3', border: '1px solid #F3EEE8' }}
              >
                <p style={{ color: '#2E2E42', fontFamily: 'Jost, sans-serif', fontWeight: '500', marginBottom: '4px' }}>
                  Monthly Members
                </p>
                <p style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}>
                  Response within <strong style={{ color: '#2E2E42' }}>5 business days</strong>
                </p>
              </div>
            </div>
            
            <p 
              className="mt-4 text-sm"
              style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}
            >
              If you contact us over the weekend, your response window begins Monday morning.
            </p>
          </div>
        </section>

        {/* What Support Covers */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(232,180,184,0.15)' }}
            >
              <Heart className="w-5 h-5" style={{ color: '#A9756B' }} />
            </div>
            <h2 
              className="text-xl font-medium"
              style={{ color: '#1A1A2E', fontFamily: 'Jost, sans-serif' }}
            >
              What Support Covers
            </h2>
          </div>
          
          <div 
            className="p-6 rounded-2xl"
            style={{ background: '#F9F4F0', border: '1px solid #F3EEE8' }}
          >
            <p 
              className="mb-4 leading-relaxed"
              style={{ color: '#2E2E42', fontFamily: 'Jost, sans-serif' }}
            >
              Support is here for:
            </p>
            
            <ul className="space-y-3 mb-6">
              {[
                'Questions about your Sage experience',
                'Technical issues with your account',
                'Guidance on navigating the healing phases',
                'Anything getting in the way of you showing up for yourself'
              ].map((item) => (
                <li 
                  key={item}
                  className="flex items-start gap-3"
                  style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}
                >
                  <span style={{ color: '#C9A84C' }}>✦</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            
            <div 
              className="p-4 rounded-xl"
              style={{ background: 'rgba(232,180,184,0.08)', border: '1px solid rgba(232,180,184,0.15)' }}
            >
              <p 
                className="font-medium mb-2"
                style={{ color: '#A9756B', fontFamily: 'Jost, sans-serif' }}
              >
                Important Note
              </p>
              <p style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif', lineHeight: '1.6' }}>
                Support is not a substitute for crisis care. If you are in emotional distress or experiencing a mental health emergency, please contact the{' '}
                <a 
                  href="https://988lifeline.org/chat/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#C9A84C', textDecoration: 'underline' }}
                >
                  988 Suicide and Crisis Lifeline
                </a>{' '}
                by calling or texting <strong>988</strong>, or go to your nearest emergency room.
              </p>
            </div>
          </div>
        </section>

        {/* How to Reach Us */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(201,168,76,0.15)' }}
            >
              <Mail className="w-5 h-5" style={{ color: '#C9A84C' }} />
            </div>
            <h2 
              className="text-xl font-medium"
              style={{ color: '#1A1A2E', fontFamily: 'Jost, sans-serif' }}
            >
              How to Reach Us
            </h2>
          </div>
          
          <div 
            className="p-6 rounded-2xl text-center"
            style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}
          >
            <p 
              className="text-sm tracking-wider mb-2"
              style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}
            >
              EMAIL
            </p>
            <a 
              href="mailto:support@mindfulrenewaltx.com"
              className="text-xl transition-opacity hover:opacity-80"
              style={{ color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}
            >
              support@mindfulrenewaltx.com
            </a>
            
            <div className="mt-6 pt-6" style={{ borderTop: '1px solid #F3EEE8' }}>
              <p style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif', fontSize: '14px' }}>
                Response window: <strong style={{ color: '#2E2E42' }}>48 hours</strong> for annual members / <strong style={{ color: '#2E2E42' }}>5 business days</strong> for monthly members
              </p>
            </div>
          </div>
          
          <p 
            className="mt-6 text-center leading-relaxed"
            style={{ color: '#4A4A5A', fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontStyle: 'italic' }}
          >
            We read every message. We respond to every message.<br />
            You will not fall through the cracks here.
          </p>
        </section>

        {/* A Note on What This Space Is */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(138,158,143,0.15)' }}
            >
              <Shield className="w-5 h-5" style={{ color: '#72826F' }} />
            </div>
            <h2 
              className="text-xl font-medium"
              style={{ color: '#1A1A2E', fontFamily: 'Jost, sans-serif' }}
            >
              A Note on What This Space Is
            </h2>
          </div>
          
          <div 
            className="p-6 rounded-2xl"
            style={{ background: '#F9F4F0', border: '1px solid #F3EEE8' }}
          >
            <p 
              className="leading-relaxed mb-4"
              style={{ color: '#2E2E42', fontFamily: 'Jost, sans-serif' }}
            >
              Sage and Mindful Renewal Coaching are not a replacement for licensed therapy or professional mental health treatment.
            </p>
            
            <p 
              className="leading-relaxed mb-4"
              style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}
            >
              What we offer is a structured, supported environment for self-reflection, emotional processing, and personal growth.
            </p>
            
            <p 
              className="leading-relaxed"
              style={{ color: '#4A4A5A', fontFamily: 'Jost, sans-serif' }}
            >
              If you're unsure whether coaching or therapy is the right fit for where you are, we're happy to help you think that through — just ask.
            </p>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center pt-8" style={{ borderTop: '1px solid #F3EEE8' }}>
          <p style={{ color: '#A3A0AE', fontFamily: 'Jost, sans-serif', fontSize: '13px' }}>
            Mindful Renewal Coaching · Houston, TX
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportPolicyPage;
